import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { useHistory, useParams } from "react-router-dom";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { Damage as NotificationDamage, IMAGE_TYPE, NotificationImage } from "src/api/Notification";
import { SubmitHandler, useForm } from "react-hook-form";
import { toJS } from "mobx";
import { ImageSelector } from "src/components/imageSelector";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SelectComponent } from "src/api/Select";
import { UNDEFINED_ROOM_TYPE, UNDEFINED_APPLIANCE_TYPE } from "src/api/Select";

export interface DamageFormFields {
    manufacturer?: string;
    serialnumber?: string;
    description?: string;
    image1?: NotificationImage;
    image2?: NotificationImage;
    serialnumberimage?: NotificationImage;
}

const DamageBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);

    const damage_schema = yup.object().shape({
        description: yup.string().required(t("form.required_field"))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm<DamageFormFields>({
        resolver: yupResolver(damage_schema)
    });

    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;

    let componentParameters = undefined;

    if (easyContactStore.notification.componentid) {
        const selectedComponent: SelectComponent | null = easyContactStore.getSelectComponentById(
            easyContactStore.notification.componentid
        );
        componentParameters = toJS(selectedComponent?.parameters);
    }

    useEffect(() => {
        if (easyContactStore.notification.damage?.image1) {
            setValue("image1", toJS(easyContactStore.notification.damage?.image1));
        }

        if (easyContactStore.notification.damage?.image2) {
            setValue("image2", toJS(easyContactStore.notification.damage?.image2));
        }

        if (easyContactStore.notification.damage?.serialnumberimage) {
            setValue("serialnumberimage", toJS(easyContactStore.notification.damage?.serialnumberimage));
        }
    }, [
        easyContactStore.notification.damage?.image1,
        easyContactStore.notification.damage?.image2,
        easyContactStore.notification.damage?.serialnumberimage,
        setValue
    ]);

    const onSubmit: SubmitHandler<DamageFormFields> = useCallback(
        async (data) => {
            const damage: NotificationDamage = {
                description: data.description,
                manufacturer: data.manufacturer,
                serialnumber: data.serialnumber,
                image1: data.image1,
                image2: data.image2,
                serialnumberimage: data.serialnumberimage,
                roomtype: easyContactStore.roomType ?? UNDEFINED_ROOM_TYPE,
                appliancetype: easyContactStore.applianceType ?? UNDEFINED_APPLIANCE_TYPE
            };

            easyContactStore.setDamage(damage);

            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    if (componentParameters?.appliancetype) {
        easyContactStore.setApplianceType(componentParameters?.appliancetype);
    }

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        {easyContactStore.notification.component && <h4>{easyContactStore.notification.component}</h4>}
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <ImageSelector.Container>
                                <ImageSelector.Item
                                    label={`${t("screens.damage.damageimage")} 1`}
                                    name="image1"
                                    imagetype={IMAGE_TYPE.DAMAGE}
                                    setValue={setValue}
                                    initialImageUrl={easyContactStore.notification.damage?.image1?.url}
                                />
                                <ImageSelector.Item
                                    label={`${t("screens.damage.damageimage")} 2`}
                                    name="image2"
                                    imagetype={IMAGE_TYPE.DAMAGE}
                                    setValue={setValue}
                                    initialImageUrl={easyContactStore.notification.damage?.image2?.url}
                                />
                            </ImageSelector.Container>
                            {componentParameters?.manufacturer ? (
                                <UI.Input
                                    {...register("manufacturer")}
                                    defaultValue={easyContactStore.notification.damage?.manufacturer || ""}
                                    label={t("screens.damage.manufacturer")}
                                    type="text"
                                />
                            ) : undefined}

                            {componentParameters?.serialnumber ? (
                                <>
                                    <UI.Input
                                        {...register("serialnumber")}
                                        defaultValue={easyContactStore.notification.damage?.serialnumber || ""}
                                        label={t("screens.damage.serialnumber")}
                                        type="text"
                                    />
                                    <ImageSelector.Container>
                                        <ImageSelector.Item
                                            label={t("screens.damage.serialnumberimage")}
                                            name="serialnumberimage"
                                            imagetype={IMAGE_TYPE.SERIALNUMBER}
                                            setValue={setValue}
                                            initialImageUrl={
                                                easyContactStore.notification.damage?.serialnumberimage?.url
                                            }
                                        />
                                    </ImageSelector.Container>
                                </>
                            ) : undefined}

                            <UI.Input
                                {...register("description")}
                                defaultValue={easyContactStore.notification.damage?.description || ""}
                                label={`${t("screens.damage.description")} *`}
                                type="text"
                                as="textarea"
                                errorMsg={errors.description ? errors.description.message : undefined}
                            />
                            <UI.Button onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Damage = Sentry.withProfiler(observer(DamageBase));
